import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, Tag } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NewsItem } from '@/types/news';
import { dataService } from '@/lib/data/DataService';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';

const News = () => {
  const [articles, setArticles] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { language, t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const newsData = await dataService.getNews();
      // Filter only published articles and sort by date
      const publishedNews = newsData
        .filter(article => article.status === 'published')
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setArticles(publishedNews);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const newsArticles = articles.filter(article => article.category === 'news');
  const eventArticles = articles.filter(article => article.category === 'event');
  const interviewArticles = articles.filter(article => article.category === 'interview');

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{language === 'en' ? 'News & Events | LeaderProMed Mauritania' : 'Actualités & Événements | LeaderProMed Mauritanie'}</title>
        <meta name="description" content={language === 'en' ? 'Find all the medical news, congresses, events and innovations in Mauritania and North Africa with LeaderProMed.' : 'Retrouvez toutes les actualités médicales, congrès, événements et innovations en Mauritanie et en Afrique du Nord avec LeaderProMed.'} />
        <meta property="og:title" content={language === 'en' ? 'News & Events | LeaderProMed Mauritania' : 'Actualités & Événements | LeaderProMed Mauritanie'} />
        <meta property="og:description" content={language === 'en' ? 'Find all the medical news, congresses, events and innovations in Mauritania and North Africa with LeaderProMed.' : 'Retrouvez toutes les actualités médicales, congrès, événements et innovations en Mauritanie et en Afrique du Nord avec LeaderProMed.'} />
        <meta property="og:type" content="article" />
      </Helmet>
      <div className="min-h-screen flex flex-col">
        <Header />
        {/* Hero Section */}
        <section className="relative flex items-center justify-center bg-cover bg-center min-h-[40vh]"
          style={{ backgroundImage: "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/background.jpg')" }}>
          <div className="text-center px-4 z-10">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">{t('news.title')}</h1>
            <p className="text-xl text-white max-w-2xl mx-auto">
              {t('news.subtitle')}
            </p>
          </div>
        </section>
        {/* News Tabs & Grid */}
        <section className="container mx-auto px-4 py-16">
          <Tabs defaultValue="news" className="space-y-4">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="news">Actualités</TabsTrigger>
              <TabsTrigger value="events">Événements</TabsTrigger>
              <TabsTrigger value="interviews">Interviews</TabsTrigger>
            </TabsList>
            <TabsContent value="news">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {newsArticles.map((article) => (
                  <Card
                    key={article.id}
                    className="bg-white shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                    onClick={() => navigate(`/news/${article.id}`)}
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter') navigate(`/news/${article.id}`); }}
                    role="button"
                    aria-label={`Voir le détail de ${article.title}`}
                  >
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={article.images?.[0] || "/background.jpg"} 
                        alt={`${article.title} - Leader ProMed Mauritanie`} 
                        className="w-full h-full object-cover transition-transform hover:scale-105"
                      />
                    </div>
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2 text-blue-600 text-sm">
                        <Calendar size={16} />
                        <span>{new Date(article.date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                      </div>
                      <CardTitle className="text-xl">{article.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-gray-600">
                        {article.content?.slice(0, 180)}{article.content && article.content.length > 180 ? '...' : ''}
                      </CardDescription>
                    </CardContent>
                    <CardFooter>
                      <Button variant="link" className="text-blue-600 p-0 hover:text-blue-800" onClick={e => { e.stopPropagation(); navigate(`/news/${article.id}`); }}>
                        Lire plus &rarr;
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="events">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {eventArticles.map((article) => (
                  <Card key={article.id} className="bg-white shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={article.images?.[0] || "/background.jpg"} 
                        alt={`${article.title} - Leader ProMed Mauritanie`} 
                        className="w-full h-full object-cover transition-transform hover:scale-105"
                      />
                    </div>
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2 text-blue-600 text-sm">
                        <Calendar size={16} />
                        <span>{new Date(article.date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                      </div>
                      <CardTitle className="text-xl">{article.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-gray-600">
                        {article.content?.slice(0, 180)}{article.content && article.content.length > 180 ? '...' : ''}
                      </CardDescription>
                    </CardContent>
                    <CardFooter>
                      <Link to={`/news/${article.id}`}>
                        <Button variant="link" className="text-blue-600 p-0 hover:text-blue-800">
                          Lire plus &rarr;
                        </Button>
                      </Link>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="interviews">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {interviewArticles.map((article) => (
                  <Card key={article.id} className="bg-white shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={article.images?.[0] || "/background.jpg"} 
                        alt={`${article.title} - Leader ProMed Mauritanie`} 
                        className="w-full h-full object-cover transition-transform hover:scale-105"
                      />
                    </div>
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2 text-blue-600 text-sm">
                        <Calendar size={16} />
                        <span>{new Date(article.date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                      </div>
                      <CardTitle className="text-xl">{article.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-gray-600">
                        {article.content?.slice(0, 180)}{article.content && article.content.length > 180 ? '...' : ''}
                      </CardDescription>
                    </CardContent>
                    <CardFooter>
                      <Link to={`/news/${article.id}`}>
                        <Button variant="link" className="text-blue-600 p-0 hover:text-blue-800">
                          Lire plus &rarr;
                        </Button>
                      </Link>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </section>
        {/* Newsletter Section */}
        <section className="bg-blue-50 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-blue-600 mb-4">{t('newsletter.title')}</h2>
              <p className="text-gray-600 mb-8">
                {t('newsletter.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <input 
                  type="email" 
                  placeholder={t('newsletter.placeholder')} 
                  className="px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex-grow max-w-md"
                />
                <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6">
                  {t('newsletter.button')}
                </Button>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};

export default News;
